/**
 * Data Hydration Service
 * Fetches all user project data on login and stores it in cache
 */

import * as cache from './localCache';
import axiosWrapper from '../apiRequests/axiosWrapper';
import { isOnline } from '../apiRequests/offlineQueue';

// Guard against concurrent hydration calls (e.g. from LoginModal and Navbar both
// triggering hydrateUserData at the same time on login).
let isHydrating = false;

/**
 * Normalize entity data
 */
const normalizeEntity = (entity) => {
  if (!entity) return null;
  if (!entity._id && !entity.id) return null;

  return {
    ...entity,
    updatedAt: entity.updatedAt || entity.createdAt || new Date().toISOString(),
  };
};

/**
 * Normalize array of entities
 */
const normalizeEntities = (entities) => {
  if (!Array.isArray(entities)) return [];
  return entities.map((entity) => normalizeEntity(entity)).filter((e) => e !== null);
};

/**
 * Clear all cached data for current user
 */
export const clearUserCache = async () => {
  try {
    await cache.clearAllCache();
    console.log('[dataHydration] User cache cleared');
    return { success: true };
  } catch (error) {
    console.error('[dataHydration] Error clearing cache:', error);
    return { success: false, error };
  }
};

/**
 * Hydrate all user data
 * Fetches all projects and their related data (chapters, scenes, notes, corkboards, timelines)
 */
export const hydrateUserData = async (userId) => {
  if (isHydrating) {
    console.log('[dataHydration] Hydration already in progress, skipping duplicate call');
    return { success: false, skipped: true };
  }

  if (!isOnline()) {
    console.warn('[dataHydration] Cannot hydrate data while offline');
    return { success: false, error: 'Offline' };
  }

  isHydrating = true;
  console.log('[dataHydration] Starting data hydration for user:', userId);

  try {
    // Step 1: Fetch all projects
    const projectsResponse = await axiosWrapper({
      method: 'get',
      path: 'projects',
    });
    if (projectsResponse.error) {
      console.error('[dataHydration] Error fetching projects:', projectsResponse.error);
      return { success: false, error: projectsResponse.error };
    }

    const projects = projectsResponse.data?.projects || projectsResponse.data?.data || [];
    const normalizedProjects = normalizeEntities(projects);

    if (normalizedProjects.length === 0) {
      console.log('[dataHydration] No projects found for user');
      // Still clear existing cache
      await clearUserCache();
      return { success: true, projects: 0, message: 'No projects found' };
    }

    // Store projects in cache
    await cache.setCachedItems('projects', normalizedProjects);
    console.log(`[dataHydration] Cached ${normalizedProjects.length} projects`);

    // Step 2: For each project, fetch related data
    const allChapters = [];
    const allScenes = [];
    const allNotes = [];
    const allCorkboards = [];
    const allTimelines = [];
    const allCharacters = [];
    const allFamilies = [];

    // Fetch data for all projects in parallel (but limit concurrency)
    const fetchPromises = normalizedProjects.map(async (project) => {
      const projectId = project._id || project.id;

      try {
        // Fetch notes
        const notesResponse = await axiosWrapper({
          method: 'get',
          path: `projects/${projectId}/notes`,
        });
        if (!notesResponse.error && notesResponse.data) {
          const notes = notesResponse.data?.notes || notesResponse.data?.data || [];
          allNotes.push(...normalizeEntities(notes));
        }

        // Fetch corkboards
        const corkboardsResponse = await axiosWrapper({
          method: 'get',
          path: `projects/${projectId}/corkboards`,
        });
        if (!corkboardsResponse.error && corkboardsResponse.data) {
          const corkboards =
            corkboardsResponse.data?.corkboards || corkboardsResponse.data?.data || [];
          allCorkboards.push(...normalizeEntities(corkboards));
        }

        // Fetch timelines
        const timelinesResponse = await axiosWrapper({
          method: 'get',
          path: `projects/${projectId}/timelines`,
        });
        if (!timelinesResponse.error && timelinesResponse.data) {
          const timelines = timelinesResponse.data?.timelines || timelinesResponse.data?.data || [];
          allTimelines.push(...normalizeEntities(timelines));
        }

        // Fetch characters
        const charactersResponse = await axiosWrapper({
          method: 'get',
          path: `projects/${projectId}/characters`,
        });
        if (!charactersResponse.error && charactersResponse.data) {
          const characters =
            charactersResponse.data?.characters || charactersResponse.data?.data || [];
          allCharacters.push(...normalizeEntities(characters));
        }

        // Fetch families
        const familiesResponse = await axiosWrapper({
          method: 'get',
          path: `projects/${projectId}/families`,
        });
        if (!familiesResponse.error && familiesResponse.data) {
          const families = familiesResponse.data?.families || familiesResponse.data?.data || [];
          allFamilies.push(...normalizeEntities(families));
        }

        // Fetch chapters (from thinChapters if available, or individually)
        const thinChapters = project.thinChapters || [];
        const chapterPromises = thinChapters.map(async (thinChapter) => {
          const chapterId = thinChapter.id || thinChapter._id;
          if (chapterId) {
            try {
              const chapterResponse = await axiosWrapper({
                method: 'get',
                path: `chapters/${chapterId}`,
              });
              if (!chapterResponse.error && chapterResponse.data) {
                const chapter = chapterResponse.data?.chapter || chapterResponse.data?.data;
                const normalized = normalizeEntity(chapter);
                if (normalized) {
                  allChapters.push(normalized);

                  // Fetch scenes for this chapter
                  try {
                    const scenesResponse = await axiosWrapper({
                      method: 'get',
                      path: `chapters/${chapterId}/scenes`,
                    });
                    if (!scenesResponse.error && scenesResponse.data) {
                      const scenes = scenesResponse.data?.scenes || [];
                      allScenes.push(...normalizeEntities(scenes));
                    }
                  } catch (sceneError) {
                    console.warn(
                      `[dataHydration] Error fetching scenes for chapter ${chapterId}:`,
                      sceneError
                    );
                  }
                }
              }
            } catch (chapterError) {
              console.warn(`[dataHydration] Error fetching chapter ${chapterId}:`, chapterError);
            }
          }
          return null;
        });

        await Promise.all(chapterPromises);
      } catch (projectError) {
        console.warn(`[dataHydration] Error fetching data for project ${projectId}:`, projectError);
      }
    });

    // Wait for all project data to be fetched
    await Promise.all(fetchPromises);

    // Step 3: Store all data in cache
    const cachePromises = [];

    if (allChapters.length > 0) {
      cachePromises.push(cache.setCachedItems('chapters', allChapters));
      console.log(`[dataHydration] Cached ${allChapters.length} chapters`);
    }

    if (allScenes.length > 0) {
      cachePromises.push(cache.setCachedItems('scenes', allScenes));
      console.log(`[dataHydration] Cached ${allScenes.length} scenes`);
    }

    if (allNotes.length > 0) {
      cachePromises.push(cache.setCachedItems('notes', allNotes));
      console.log(`[dataHydration] Cached ${allNotes.length} notes`);
    }

    if (allCorkboards.length > 0) {
      cachePromises.push(cache.setCachedItems('corkboards', allCorkboards));
      console.log(`[dataHydration] Cached ${allCorkboards.length} corkboards`);
    }

    if (allTimelines.length > 0) {
      cachePromises.push(cache.setCachedItems('timelines', allTimelines));
      console.log(`[dataHydration] Cached ${allTimelines.length} timelines`);
    }

    if (allCharacters.length > 0) {
      cachePromises.push(cache.setCachedItems('characters', allCharacters));
      console.log(`[dataHydration] Cached ${allCharacters.length} characters`);
    }

    if (allFamilies.length > 0) {
      cachePromises.push(cache.setCachedItems('families', allFamilies));
      console.log(`[dataHydration] Cached ${allFamilies.length} families`);
    }

    await Promise.all(cachePromises);

    console.log('[dataHydration] Data hydration complete', {
      projects: normalizedProjects.length,
      chapters: allChapters.length,
      characters: allCharacters.length,
      families: allFamilies.length,
      scenes: allScenes.length,
      notes: allNotes.length,
      corkboards: allCorkboards.length,
      timelines: allTimelines.length,
    });

    return {
      success: true,
      stats: {
        projects: normalizedProjects.length,
        chapters: allChapters.length,
        scenes: allScenes.length,
        notes: allNotes.length,
        corkboards: allCorkboards.length,
        timelines: allTimelines.length,
      },
    };
  } catch (error) {
    console.error('[dataHydration] Error during data hydration:', error);
    return { success: false, error };
  } finally {
    isHydrating = false;
  }
};

export default {
  hydrateUserData,
  clearUserCache,
};
